import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ArrowLeft, Search, LayoutDashboard, Package, RefreshCw, Plus, Filter, Download, FileText, Users, BarChart3, Activity, DollarSign } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { motion } from "framer-motion";
import { getWorkflowResultsp2p } from "../../../ApiCalls/mantisFlowsManangment";
import { validateRequestWithAI } from "../../../ApiCalls/mantisFlowsApprovalRules";
import { useToast } from "../../components/ui/use-toast";
import { Toaster } from "../../components/ui/toaster";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ControlPanelDetail from "./ControlPanelDetail";
import Breadcrumbs from "./components/Breadcrumbs";
import ItemsSection from "./Section/ItemsSection";
import { connect } from "react-redux";
import { selectAuthState } from "../../../store/selectors/authSelectors";
import ControlPanelTable from "./components/ControlPanelTable";
import ControlPanelStats from "./components/ControlPanelStats";
import { useParams, useNavigate } from "react-router-dom";

function ControlPanel(props) {
  // Log información del usuario para debugging
  console.log("🔍 ControlPanel - Usuario actual:", {
    userId: props.user?._id,
    email:
      props.user?.email ||
      props.user?.corporateEmail ||
      props.user?.personalEmail,
    documentId: props.user?.documentId,
    mantisBusiness: props.mantisBusiness?._id,
  });
  const { iprId } = useParams(); // Extraer el ID del IPR de la URL
  const navigate = useNavigate();
  const [currentMainView, setCurrentMainView] = useState("controlPanel"); // 'controlPanel' o 'items'
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: "Home", href: "/" },
    { label: "Control Panel", href: "/business/backoffice/control-panel-p2o" }, // Se actualizará dinámicamente
  ]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const { toast } = useToast();

  // Función para cargar los datos
  const fetchData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setInitialLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const response = await getWorkflowResultsp2p(props.mantisBusiness._id);

      if (response.success) {
        const formattedResults = response.data.map((result) => {
          const formattedResult = { ...result };

          if (result.extraction_date) {
            formattedResult.extraction_date = new Date(
              result.extraction_date
            ).toLocaleString();
          }

          if (result.total_amount) {
            formattedResult.total_amount = `$${parseFloat(
              result.total_amount
            ).toLocaleString("es-CO")}`;
          }

          formattedResult.hasErrors = result.status === "rejected";
          formattedResult.isVerified = result.status === "complete";
          formattedResult.status = mapValidationStatus(result.status);
          formattedResult.date = result.extraction_date;

          return formattedResult;
        });

        setData(formattedResults);

        // Si hay un iprId en la URL, buscar y seleccionar ese IPR automáticamente
        if (iprId && formattedResults.length > 0) {
          const foundRecord = formattedResults.find(
            (record) => record.id === iprId
          );
          if (foundRecord) {
            await handleRecordSelect(foundRecord, false); // false para no navegar
          } else {
            // IPR no encontrado, mostrar mensaje y redirigir
            toast({
              title: "IPR no encontrado",
              description: `No se encontró el IPR con ID: ${iprId}`,
              variant: "destructive",
            });
            navigate("/business/backoffice/control-panel-p2o", {
              replace: true,
            });
          }
        }
      } else {
        setError("No se pudieron cargar los datos");
        toast({
          title: "Error",
          description: "No se pudieron cargar los datos",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("Error al cargar los datos:", err);
      setError("Error al cargar los datos");
      toast({
        title: "Error",
        description: "Error al cargar los datos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  // Función para mapear los estados de validación
  const mapValidationStatus = (status) => {
    switch (status) {
      case "Validado":
        return "complete";
      case "Aprobado":
        return "approved";
      case "approved":
        return "approved";
      case "Pendiente":
        return "pending";
      case "Rechazado":
        return "error";
      default:
        return "pending";
    }
  };

  useEffect(() => {
    fetchData(true);
  }, []);

  // Actualizar breadcrumbs cuando cambia la vista principal o el registro seleccionado
  useEffect(() => {
    const baseBreadcrumbs = [{ label: "Home", href: "/" }];
    if (currentMainView === "controlPanel") {
      if (selectedRecord) {
        setBreadcrumbs([
          ...baseBreadcrumbs,
          {
            label: "Control Panel",
            href: "/business/backoffice/control-panel-p2o",
            isViewSwitcher: true,
            view: "controlPanel",
          },
          { label: `${selectedRecord.title}`, href: "#" },
        ]);
      } else {
        setBreadcrumbs([
          ...baseBreadcrumbs,
          {
            label: "Control Panel",
            href: "/business/backoffice/control-panel-p2o",
            isViewSwitcher: true,
            view: "controlPanel",
          },
        ]);
      }
    } else if (currentMainView === "items") {
      setBreadcrumbs([
        ...baseBreadcrumbs,
        { label: "Items", href: "#", isViewSwitcher: true, view: "items" },
      ]);
      setSelectedRecord(null); // Limpiar registro seleccionado si cambiamos a Items
    }
  }, [currentMainView, selectedRecord]);

  // Breadcrumbs navegación
  const handleBreadcrumbNavigate = (href, item) => {
    if (item?.isViewSwitcher) {
      setCurrentMainView(item.view);
      if (item.view === "controlPanel") {
        setSelectedRecord(null);
        // Si estamos en una vista de detalle, navegar de vuelta a la vista principal
        if (iprId) {
          navigate("/business/backoffice/control-panel-p2o", { replace: true });
        }
      }
      return; // Evitar navegación si es un switcher de vista
    }
    if (
      href === "/business/backoffice/control-panel-p2o" ||
      (currentMainView === "controlPanel" && !selectedRecord)
    ) {
      setCurrentMainView("controlPanel");
      setSelectedRecord(null);
      navigate("/business/backoffice/control-panel-p2o", { replace: true });
    }
    // Aquí puedes añadir lógica para navegación de breadcrumbs de Items si es necesario
  };

  // Función para verificar si el usuario actual puede aprobar un step
  const canUserApproveStep = (step, currentUser, approvals) => {
    if (!step || !currentUser) return false;
    console.log("🔍 currentUserBruh:", currentUser);
    // Verificar si el usuario actual es el responsable del step
    // Comparar solo por email (responsible)
    const isResponsible =
      step.responsible === currentUser.email ||
      step.responsible === currentUser.corporateEmail ||
      step.responsible === currentUser.personalEmail;
    // Verificar si el usuario ya ha aprobado el step
    const hasApproved = approvals.some(
      (approval) =>
        approval.approving_user_id === currentUser._id &&
        approval.status === "approved"
    );

    return isResponsible && !hasApproved;
  };

  const stepApproved = (step, currentUser, approvals) => {
    if (!step || !currentUser) return false;
    const hasApproved = approvals.some(
      (approval) =>
        approval.approving_user_id === step._id &&
        approval.status === "approved"
    );
    return hasApproved;
  };

  const stepRejected = (step, currentUser, approvals) => {
    if (!step || !currentUser) return false;
    const hasRejected = approvals.some(
      (approval) =>
        approval.approving_user_id === step._id &&
        approval.status === "rejected"
    );
    return hasRejected;
  };

  // Función para manejar la selección de un registro
  const handleRecordSelect = async (record, shouldNavigate = true) => {
    try {
      // Validar el request con IA antes de mostrar el detalle
      //const validationResult = await validateRequestWithAI(props.mantisBusiness._id, record.id);

      // Verificar permisos del usuario para cada step del approval_plan
      let userApprovalSteps = [];
      if (record.approval_plan && Array.isArray(record.approval_plan)) {
        userApprovalSteps = record.approval_plan.map((step) => ({
          ...step,
          canApprove: canUserApproveStep(
            step,
            props.user,
            record.approvals || []
          ),
          stepApproved: stepApproved(step, props.user, record.approvals || []),
          stepRejected: stepRejected(step, props.user, record.approvals || []),
        }));
      }

      // Agregar los datos de validación al record
      const enrichedRecord = {
        ...record,
        //validationData: validationResult.data || validationResult,
        userApprovalSteps: userApprovalSteps,
        currentUserId: props.user._id,
        currentUserEmail:
          props.user.email ||
          props.user.corporateEmail ||
          props.user.personalEmail,
      };
      setSelectedRecord(enrichedRecord);

      // Navegar a la URL con el ID del IPR
      if (shouldNavigate) {
        navigate(`/business/backoffice/control-panel-p2o/${record.id}`, {
          replace: true,
        });
      }

      // Notificar si el usuario puede aprobar algún step
      const canApproveCount = userApprovalSteps.filter(
        (step) => step.canApprove
      ).length;
      if (canApproveCount > 0) {
        toast({
          title: "Permisos de aprobación",
          description: `Puedes aprobar ${canApproveCount} paso(s) en este workflow`,
          variant: "default",
        });
      }

      // Log completo de la selección de IPR para debugging
      console.log("📋 IPR Seleccionado - Resumen completo:", {
        iprId: record.id,
        totalSteps: userApprovalSteps.length,
        stepsUserCanApprove: canApproveCount,
        userDetails: {
          id: props.user._id,
          email:
            props.user.email ||
            props.user.corporateEmail ||
            props.user.personalEmail,
          documentId: props.user.documentId,
        },
        approvalStepsDetail: userApprovalSteps.map((step) => ({
          step: step.step,
          responsible: step.responsible,
          action: step.action,
          canUserApprove: step.canApprove,
          documentId: step.documentId,
        })),
      });
    } catch (error) {
      console.error("Error al validar el request:", error);

      // En caso de error, mostrar el detalle sin datos de validación pero con permisos
      let userApprovalSteps = [];
      if (record.approval_plan && Array.isArray(record.approval_plan)) {
        userApprovalSteps = record.approval_plan.map((step) => ({
          ...step,
          canApprove: canUserApproveStep(
            step,
            props.user,
            record.approvals || []
          ),
          stepApproved: stepApproved(step, props.user, record.approvals || []),
          stepRejected: stepRejected(step, props.user, record.approvals || []),
        }));
      }

      const enrichedRecord = {
        ...record,
        userApprovalSteps: userApprovalSteps,
        currentUserId: props.user._id,
        currentUserEmail:
          props.user.email ||
          props.user.corporateEmail ||
          props.user.personalEmail,
      };

      setSelectedRecord(enrichedRecord);

      // Navegar a la URL con el ID del IPR
      if (shouldNavigate) {
        navigate(`/business/backoffice/control-panel-p2o/${record.id}`, {
          replace: true,
        });
      }

      toast({
        title: "Advertencia",
        description: "No se pudieron cargar las reglas de validación",
        variant: "destructive",
      });
    }

    // Breadcrumbs se actualizan por el useEffect de [currentMainView, selectedRecord]
  };

  // Función para manejar la edición de un IPR
  const handleEditIPR = (record) => {
    toast({
      title: "Editar IPR",
      description: `Editando registro: ${record.ip || record.id || "N/A"}`,
      variant: "default",
    });
    // Aquí puedes implementar la lógica de edición
    // Por ejemplo, abrir un modal de edición o navegar a una página de edición
    console.log("Editando IPR:", record);
  };

  // Función para manejar la eliminación de un IPR
  const handleDeleteIPR = (record) => {
    toast({
      title: "Eliminar IPR",
      description: `¿Estás seguro de que quieres eliminar el registro: ${
        record.ip || record.id || "N/A"
      }?`,
      variant: "destructive",
    });
    // Aquí puedes implementar la lógica de eliminación
    // Por ejemplo, mostrar un modal de confirmación
    console.log("Eliminando IPR:", record);
  };

  const handleViewSwitch = (view) => {
    setCurrentMainView(view);
    if (view === "items") {
      setSelectedRecord(null); // Limpiar si se va a items y había algo seleccionado
      // Si estamos en una vista de detalle, navegar de vuelta a la vista principal
      if (iprId) {
        navigate("/business/backoffice/control-panel-p2o", { replace: true });
      }
    }
  };

  // Función para manejar el botón "Back" del detalle
  const handleBackFromDetail = () => {
    setSelectedRecord(null);
    navigate("/business/backoffice/control-panel-p2o", { replace: true });
  };

  // Filtrar datos basado en el término de búsqueda
  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return Object.values(item).some(
      (value) => value && value.toString().toLowerCase().includes(searchLower)
    );
  });

  // Función para obtener estadísticas rápidas
  const getQuickStats = () => {
    const total = data.length;
    const pending = data.filter((item) => item.status === "pending").length;
    const completed = data.filter((item) => item.status === "complete").length;
    const errors = data.filter((item) => item.status === "error").length;

    // Cálculos adicionales para estadísticas más útiles
    const pendingPercentage =
      total > 0 ? Math.round((pending / total) * 100) : 0;
    const completedPercentage =
      total > 0 ? Math.round((completed / total) * 100) : 0;
    const errorPercentage = total > 0 ? Math.round((errors / total) * 100) : 0;

    // Estadísticas por canal
    const formChannels = data.filter(
      (item) => item.origin_channel?.toLowerCase() === "form"
    ).length;
    const whatsappChannels = data.filter(
      (item) => item.origin_channel?.toLowerCase() === "whatsapp"
    ).length;

    // IPRs de hoy
    const today = new Date();
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const todayItems = data.filter((item) => {
      if (!item.extraction_date) return false;
      const itemDate = new Date(item.extraction_date);
      return itemDate >= todayStart;
    }).length;

    // Monto total (si está disponible)
    const totalAmount = data.reduce((sum, item) => {
      if (item.total_amount && typeof item.total_amount === "string") {
        const amount = parseFloat(item.total_amount.replace(/[^\d.-]/g, ""));
        return sum + (isNaN(amount) ? 0 : amount);
      }
      return sum;
    }, 0);

    return {
      total,
      pending,
      completed,
      errors,
      pendingPercentage,
      completedPercentage,
      errorPercentage,
      formChannels,
      whatsappChannels,
      todayItems,
      totalAmount,
    };
  };

  const stats = getQuickStats();

  // Función para exportar a Excel
  const exportToExcel = () => {
    try {
      // Preparar los datos para exportar
      const exportData = filteredData.map((item) => {
        const cleanItem = {};

        // Mapping de campos con nombres más amigables
        const fieldMapping = {
          id: "ID",
          friendly_id: "ID Amigable",
          origin_channel: "Canal",
          validation_status: "Estado de Validación",
          total_amount: "Monto Total",
          extraction_date: "Fecha de Extracción",
          created_by: "Creador",
          created_at: "Fecha de Creación",
          area: "Área",
          title: "Título",
          source_email: "Email Origen",
          language: "Idioma",
        };

        // Procesar solo los campos que queremos exportar
        Object.keys(item).forEach((key) => {
          if (fieldMapping[key]) {
            let value = item[key];

            // Limpiar datos para Excel
            if (value === null || value === undefined) {
              value = "Sin datos";
            } else if (typeof value === "object") {
              value = "Objeto complejo";
            } else if (
              (key === "extraction_date" || key === "created_at") &&
              value
            ) {
              // Formatear fecha para Excel de manera más legible
              try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  value = date.toLocaleDateString("es-ES", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                }
              } catch (error) {
                // Mantener valor original si no se puede parsear
              }
            } else if (key === "total_amount" && typeof value === "string") {
              // Limpiar formato de moneda para Excel
              value = value.replace(/[^\d.-]/g, "");
            } else if (key === "created_by" && typeof value === "string") {
              // Limpiar created_by para mostrar solo el nombre sin ID
              if (value.includes("(") && value.includes(")")) {
                value = value.replace(/\s*\([^)]*\)/, "").trim();
              }
            } else if (key === "origin_channel") {
              // Traducir canales para mejor legibilidad
              const channelTranslations = {
                form: "Formulario",
                whatsapp: "WhatsApp",
                email: "Email",
              };
              value = channelTranslations[value?.toLowerCase()] || value;
            }

            cleanItem[fieldMapping[key]] = value;
          }
        });

        return cleanItem;
      });

      // Crear archivo CSV (más compatible que Excel puro)
      const csvContent = convertToCSV(exportData);
      downloadCSV(
        csvContent,
        `IPRs_Export_${new Date().toISOString().split("T")[0]}.csv`
      );

      toast({
        title: "Exportación Exitosa",
        description: `Se exportaron ${exportData.length} registros correctamente`,
        variant: "default",
      });
    } catch (error) {
      console.error("Error al exportar:", error);
      toast({
        title: "Error de Exportación",
        description: "No se pudo exportar el archivo",
        variant: "destructive",
      });
    }
  };

  // Función para convertir datos a CSV
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";

    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(",");

    const csvRows = data.map((row) => {
      return headers
        .map((header) => {
          let value = row[header];
          // Escapar comillas y comas en CSV
          if (typeof value === "string") {
            value = value.replace(/"/g, '""');
            if (
              value.includes(",") ||
              value.includes('"') ||
              value.includes("\n")
            ) {
              value = `"${value}"`;
            }
          }
          return value;
        })
        .join(",");
    });

    return [csvHeaders, ...csvRows].join("\n");
  };

  // Función para descargar CSV
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Renderizado del contenido principal
  const renderMainContent = () => {
    if (currentMainView === "items") {
      return <ItemsSection mantisBusiness={props.mantisBusiness} />;
    }

    // Lógica existente para Control Panel
    if (!selectedRecord) {
      return (
        <div className="w-full max-w-none">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
          >
            {/* Enhanced Stats Cards - Temporarily Commented Out */}
            {/* 
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="card-clean p-6 relative overflow-hidden"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                      Total IPRs
                    </p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                      {stats.total}
                    </p>
                    <div className="flex items-center text-sm">
                      <span className="text-blue-600 dark:text-blue-400 font-medium">
                        {stats.todayItems} hoy
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded">
                    <FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="card-clean p-6 relative overflow-hidden"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                      En Proceso
                    </p>
                    <p className="text-3xl font-bold text-amber-600 dark:text-amber-400 mb-2">
                      {stats.pending}
                    </p>
                    <div className="flex items-center text-sm">
                      <span className="text-amber-600 dark:text-amber-400 font-medium">
                        {stats.pendingPercentage}% del total
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded">
                    <Activity className="h-8 w-8 text-amber-600 dark:text-amber-400" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-amber-600"></div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="card-clean p-6 relative overflow-hidden"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                      Completados
                    </p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                      {stats.completed}
                    </p>
                    <div className="flex items-center text-sm">
                      <span className="text-green-600 dark:text-green-400 font-medium">
                        {stats.completedPercentage}% exitoso
                      </span>
                    </div>
                  </div>
                  <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded">
                    <BarChart3 className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-green-600"></div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="card-clean p-6 relative overflow-hidden"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {stats.totalAmount > 0 ? (
                      <>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                          Valor Total
                        </p>
                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mb-2">
                          ${stats.totalAmount.toLocaleString('es-CO')}
                        </p>
                        <div className="flex items-center text-sm">
                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                            COP procesados
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                          Canales Activos
                        </p>
                        <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                          {stats.formChannels + stats.whatsappChannels}
                        </p>
                        <div className="flex items-center text-sm space-x-3">
                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                            📝 {stats.formChannels}
                          </span>
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            💬 {stats.whatsappChannels}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded">
                    {stats.totalAmount > 0 ? (
                      <DollarSign className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <Users className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                    )}
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-purple-600"></div>
              </motion.div>
            </div>
            */}

            {/* Modern Table Container */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="card-clean overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700">
                <div className="px-6 py-5">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-orange-500 rounded">
                        <LayoutDashboard className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                          Panel de Control IPRs
                        </h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          Gestiona y monitorea todas las solicitudes de compra
                          interna
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Buscar IPRs..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 h-10 w-64 focus:ring-2 focus:ring-orange-500 border-slate-300 dark:border-slate-600"
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="btn-clean btn-ghost h-10 px-4"
                        >
                          <Filter className="h-4 w-4 mr-2" />
                          Filtros
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={exportToExcel}
                          disabled={loading || filteredData.length === 0}
                          className="btn-clean btn-ghost h-10 px-4"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Exportar ({filteredData.length})
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchData(false)}
                          disabled={loading}
                          className="btn-clean btn-secondary h-10 px-4"
                        >
                          <RefreshCw
                            className={`h-4 w-4 mr-2 ${
                              loading ? "animate-spin" : ""
                            }`}
                          />
                          Actualizar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <ControlPanelTable
                data={filteredData}
                loading={loading}
                onRowClick={handleRecordSelect}
                onRefresh={() => fetchData(false)}
                onEdit={handleEditIPR}
                onDelete={handleDeleteIPR}
                clickableField="ip"
              />
            </motion.div>
          </motion.div>
        </div>
      );
    }

    return (
      <div className="w-full max-w-none">
        <ControlPanelDetail
          data={selectedRecord}
          clientId={props.mantisBusiness._id}
          currentUser={props.user}
          mantisBusiness={props.mantisBusiness}
          onBack={handleBackFromDetail}
          token={props.token}
        />
      </div>
    );
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="loading-clean">
            <div className="spinner"></div>
          </div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">
            Cargando Panel de Control...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="alert-clean alert-error p-6 rounded max-w-md">
            <h3 className="font-semibold mb-2">Error de Conexión</h3>
            <p>{error}</p>
            <Button onClick={fetchData} className="btn-clean btn-primary mt-4">
              Reintentar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 w-full">
      <div className="p-4 lg:p-6 w-full space-y-6">
        <div className="max-w-full">
          <Breadcrumbs
            items={breadcrumbs}
            onNavigate={handleBreadcrumbNavigate}
          />

          {/* Selector de Vistas Principales - Solo mostrar cuando no hay registro seleccionado */}
          {!selectedRecord && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6"
            >
              <div className="tabs-clean">
                <div className="tab-list bg-white dark:bg-slate-800 rounded p-1 shadow-sm border border-slate-200 dark:border-slate-700">
                  <button
                    className={`tab-trigger ${
                      currentMainView === "controlPanel" ? "active" : ""
                    }`}
                    onClick={() => handleViewSwitch("controlPanel")}
                  >
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    <span>Control Panel</span>
                  </button>
                  <button
                    className={`tab-trigger ${
                      currentMainView === "items" ? "active" : ""
                    }`}
                    onClick={() => handleViewSwitch("items")}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    <span>Items</span>
                  </button>
                </div>
              </div>

              {props.mantisBusiness.clientType === "FORM_P2P" && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    window.open("/form/kks5r9c22o7l44oiifh3r", "_blank");
                  }}
                  className="btn-clean btn-primary h-10 px-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  <span>Crear IPR</span>
                </Button>
              )}
            </motion.div>
          )}

          {renderMainContent()}
        </div>

        {/* Toaster for Shadcn UI toast notifications */}
        <Toaster />

        {/* Toast Container for react-toastify (for compatibility) */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </div>
  );
}

export default connect(selectAuthState)(ControlPanel);
